import { isNil, trim } from "lodash";
import { EMPTY } from "constants/common";
import { AiccTtsProviderVoiceResponse, AiccTtsVoiceResolutionResponse } from "types/dto/response/aiccLearningAssets";
import { AICC_TTS_MODEL, AICC_TTS_TARGET, AiccTtsTarget } from "types/model/aiccLearningAssets";

export const TTS_FIELD_GUIDE_INTRO =
  "Converts the AI’s text responses into natural-sounding audio so you can hear correct pronunciation and " +
  "practice listening comprehension.";

export const TTS_FIELD_GUIDE = [
  {
    label: "Active TTS Model",
    description:
      "The ElevenLabs model that speaks the AI’s replies. It can be changed on the working version only, and " +
      "goes live when that version is published. Pronunciation clips follow the model you pick: Elevenlabs_v4 " +
      "uses Elevenlabs_v4; Elevenlabs_v3 and Elevenlabs_v2_5_flash use Elevenlabs_v2_5_flash.",
  },
  {
    label: "Base Speed Calibration",
    description:
      "Different voices naturally read at different paces. So when learners pick the same speed setting in the " +
      "app, one voice may sound comfortable while another feels too fast or too slow. This makes it hard for " +
      "learners to trust the speed control. Each voice’s base speed below is always applied, giving it its own " +
      "baseline pace so learners hear a consistent speed across every voice.",
  },
  {
    label: "TTS Voices",
    description:
      "Per-gender ElevenLabs voice IDs. Main is used first; fallback is used if main fails. Narrator Voice " +
      "applies on the lesson review only.",
  },
];

export const TTS_ACTIVE_MODEL_LABEL = "Active TTS Model";

export const TTS_VOICES_TITLE = "TTS Voices";

export const TTS_VOICE_ID_REQUIRED = "Voice ID is required.";

export const TTS_BASE_SPEED_REQUIRED = "Base speed is required.";

export const TTS_VOICE_ID_NOT_FOUND = "ElevenLabs has no voice with this id. It cannot be saved.";

export const TTS_SAME_VOICE_ERROR =
  "Primary and Fallback cannot be set to the same voice. Please select a different fallback voice to ensure proper " +
  "system redundancy.";

const TTS_MODEL_LABEL: Record<string, string> = {
  [AICC_TTS_MODEL.flashV2_5]: "Elevenlabs_v2_5_flash",
  [AICC_TTS_MODEL.v3]: "Elevenlabs_v3",
  [AICC_TTS_MODEL.v4]: "Elevenlabs_v4",
};

export const showTtsModel = (model?: string) => (model ? TTS_MODEL_LABEL[model] ?? model : EMPTY);

export const TTS_MODEL_NOT_SELECTABLE = "Current model — not offered for selection for this language.";

export const TTS_TARGET_LABEL: Record<AiccTtsTarget, string> = {
  [AICC_TTS_TARGET.main]: "Main",
  [AICC_TTS_TARGET.fallback]: "Fallback",
};

export const BASE_SPEED_BOUNDS = { min: 0.8, max: 1.2 };

export const BASE_SPEED_STEP = 0.01;

export const DEFAULT_BASE_SPEED = 1;

export const formatBaseSpeed = (baseSpeed: number) => baseSpeed.toFixed(2);

export const showBaseSpeed = (baseSpeed?: number) => (isNil(baseSpeed) ? EMPTY : formatBaseSpeed(baseSpeed));

/** The slider sums its own step in binary float, so a raw value can arrive as 0.8300000000000001. */
export const snapToStep = (value: number) =>
  Number(formatBaseSpeed(Math.round(value / BASE_SPEED_STEP) * BASE_SPEED_STEP));

export type VoicesById = Record<string, AiccTtsProviderVoiceResponse>;

export type VoiceResolutions = Record<string, AiccTtsVoiceResolutionResponse>;

/** Only an id the vendor answered "no such voice" for counts; one still being looked up does not. */
export const isVoiceIdNotFound = (
  voiceId: string | undefined,
  voicesById: VoicesById,
  resolutions: VoiceResolutions
) => {
  const id = trim(voiceId);
  return !!id && !voicesById[id] && resolutions[id]?.savable === false;
};

export const voiceNameOf = (voiceId: string | undefined, voicesById: VoicesById, resolutions: VoiceResolutions) => {
  const id = trim(voiceId);
  return voicesById[id]?.name ?? resolutions[id]?.name ?? undefined;
};
